import { Component, DestroyRef, effect, inject, input, linkedSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { BrnSelectImports } from '@spartan-ng/brain/select';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { HlmSelectImports } from '@spartan-ng/helm/select';
import { toast } from 'ngx-sonner';
import { v7 as uuidv7 } from 'uuid';

import { LabelRequiredDirective } from '@shared/directives/label-required.directive';
import { SidepanelService } from '../../../../shared/components/sidepanel/sidepanel.service';
import { ToastrSuccessComponent } from '../../../../shared/components/toastr/toastr-success.component';
import { Caractéristique } from '../models/caractéristique.model';
import { CaracteristiqueUpdateRequest } from '../requests/caractéristique-update-request';
import { CaractéristiqueStore } from '../store/caractéristique.store';
import { Référentiel } from '../../../../administration/gestion-référentiels/models/référentiel.model';

@Component({
  selector: 'app-form-sous-caracteristique',
  imports: [
    ReactiveFormsModule,
    HlmFieldImports,
    HlmSelectImports,
    BrnSelectImports,
    HlmInputImports,
    LabelRequiredDirective,
  ],
  templateUrl: './form-sous-caracteristique.component.html',
})
export class FormSousCaracteristiqueComponent {
  readonly #fb = inject(FormBuilder);
  readonly #sidepanelService = inject(SidepanelService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #caracteristiqueStore = inject(CaractéristiqueStore);

  readonly sousCaracteristique = input.required<Caractéristique>();
  readonly referentiel = input.required<Référentiel>();

  readonly caracteristiques = linkedSignal(() => this.#caracteristiqueStore.caracteristiques());

  readonly form = this.#fb.nonNullable.group({
    idCaracteristiqueParente: [null],
    nom: [null, Validators.required],
  });

  constructor() {
    effect(() => {
      const sousCaracteristique = this.sousCaracteristique();

      this.idCaracteristiqueParente.setValue(sousCaracteristique.idCaracteristiqueParente);
      this.nom.setValue(sousCaracteristique.nom);
    });

    this.#sidepanelService.submit.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => this.onSubmit());
  }

  onSubmit() {
    if (this.nom.hasError('required')) {
      return;
    }

    const toastrId = uuidv7();

    const sousCaractéristique: CaracteristiqueUpdateRequest = {
      idCaracteristiqueParente: this.idCaracteristiqueParente.value,
      nom: this.nom.value,
    };

    this.#caracteristiqueStore.modifier({
      idCaracteristique: this.sousCaracteristique().id,
      data: sousCaractéristique,
      next: () => {
        toast.custom(ToastrSuccessComponent, {
          componentProps: {
            message: 'Modification de la sous-caractéristique réussie',
            id: toastrId,
          },
          id: toastrId,
        });
        this.#caracteristiqueStore.charger(this.referentiel().id);
        this.#sidepanelService.close();
      },
      error: () => this.alreadyExists(sousCaractéristique),
    });
  }

  alreadyExists(sousCaractéristique: CaracteristiqueUpdateRequest) {
    const sousCaractéristiques = this.caracteristiques().find(
      (x) => x.id === this.idCaracteristiqueParente.value,
    )?.sousCaracteristiques;

    if (!sousCaractéristiques) return;

    const doublonsAvecSousCaractéristiquesExistants = sousCaractéristiques.filter(
      (x) => sousCaractéristique.nom === x.nom,
    );

    if (doublonsAvecSousCaractéristiquesExistants.length > 0) {
      this.nom.setErrors({ notUnique: true });
    }
  }

  get idCaracteristiqueParente() {
    return this.form.get('idCaracteristiqueParente') as AbstractControl;
  }

  get nom() {
    return this.form.get('nom') as AbstractControl;
  }
}
